import React from 'react';
import { useUser } from '@clerk/clerk-react';

const NetWorth = () => {
  const { user } = useUser();

  // Mock data - replace with actual data from your API
  const mockNetWorthData = {
    total: 125000,
    assets: {
      cash: 35000,
      investments: 65000,
      property: 20000,
      other: 5000
    },
    liabilities: {
      creditCards: 2500,
      loans: 10000,
      mortgage: 0
    },
    history: [
      { month: 'Jan', value: 115000 },
      { month: 'Feb', value: 118000 },
      { month: 'Mar', value: 120000 },
      { month: 'Apr', value: 119000 },
      { month: 'May', value: 122000 },
      { month: 'Jun', value: 125000 },
    ]
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateTotalAssets = () => {
    return Object.values(mockNetWorthData.assets).reduce((sum, value) => sum + value, 0);
  };

  const calculateTotalLiabilities = () => {
    return Object.values(mockNetWorthData.liabilities).reduce((sum, value) => sum + value, 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Net Worth</h1>
        <p className="mt-1 text-sm text-gray-500">Your complete financial picture at a glance</p>
      </div>

      {/* Net Worth Summary */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-500">Total Net Worth</h2>
            <p className="text-4xl font-bold text-indigo-600">{formatCurrency(mockNetWorthData.total)}</p>
            <p className="text-sm text-green-500 mt-1">+{formatCurrency(10000)} (8.7%) from last month</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              Add Account
            </button>
          </div>
        </div>

        {/* Simple bar chart placeholder */}
        <div className="h-48 flex items-end space-x-1 mt-8">
          {mockNetWorthData.history.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div 
                className="w-full bg-indigo-100 rounded-t-sm" 
                style={{ height: `${(item.value / 130000) * 100}%` }}
              ></div>
              <span className="text-xs text-gray-500 mt-1">{item.month}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Assets */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4">Assets</h2>
          <div className="space-y-4">
            {Object.entries(mockNetWorthData.assets).map(([asset, value]) => (
              <div key={asset} className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center text-green-600 mr-3">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 capitalize">{asset}</p>
                    <p className="text-sm text-gray-500">{formatCurrency(value)}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  {Math.round((value / calculateTotalAssets()) * 100)}%
                </span>
              </div>
            ))}
            <div className="pt-4 mt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <p className="font-medium">Total Assets</p>
                <p className="font-bold">{formatCurrency(calculateTotalAssets())}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Liabilities */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4">Liabilities</h2>
          <div className="space-y-4">
            {Object.entries(mockNetWorthData.liabilities).map(([debt, value]) => (
              <div key={debt} className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center text-red-600 mr-3">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 capitalize">{debt.split(/(?=[A-Z])/).join(' ')}</p>
                    <p className="text-sm text-gray-500">{formatCurrency(value)}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  {Math.round((value / (calculateTotalLiabilities() || 1)) * 100)}%
                </span>
              </div>
            ))}
            <div className="pt-4 mt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <p className="font-medium">Total Liabilities</p>
                <p className="font-bold">{formatCurrency(calculateTotalLiabilities())}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetWorth;
